import { ReactComponent as CloseIcon } from '../assets/images/icons/mevo/close-banner-icon.svg';
import { Box, Flex, Link } from '@chakra-ui/react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useIsMobile } from '../helpers/useIsMobile';
import { extractPatientAge } from '../helpers/patientAge';
import { fetchActiveBanners, BannerData, CampaignFilters, fetchDoctorRqes } from '../shared/bannerApi';
import { TrackerEventService } from '../containers/prescription/trackerEventService';
import { DateTime } from 'luxon';
import { RQE } from '../interfaces/users';
import { logger } from '../helpers/logger';
import { amplitudeModule } from '../shared/amplitude';

type BannerComponentProps = {
  closeBanner: () => void;
  partnerId: number;
  partnerName?: string;
  establishmentName?: string;
  patientAge: string;
  doctorSpecialities?: RQE[];
  prescriptionId?: number;
  prescriberId?: number;
  validationCode?: string;
};

const BannerComponent = ({
  closeBanner,
  partnerId,
  partnerName,
  establishmentName,
  patientAge,
  doctorSpecialities,
  prescriptionId,
  prescriberId,
  validationCode,
}: BannerComponentProps) => {
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasTrackedBanner = useRef(false);

  const isMobile = useIsMobile();

  const fetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: CampaignFilters = {
        partner: [partnerId.toString()],
      };

      if (patientAge) {
        filters.age = extractPatientAge(patientAge) ?? '0';
      }

      const rqes = doctorSpecialities?.map((rqe) => rqe.idRqe).filter((idRqe) => idRqe != null);

      if (rqes?.length === 0 && prescriberId) {
        const rqeResponse = await fetchDoctorRqes(prescriberId);
        if (rqeResponse?.data?.length > 0) {
          filters.speciality = rqeResponse.data.map((rqe: any) => rqe.idRqe).filter((idRqe: string) => idRqe != null);
        }
      } else {
        filters.speciality = rqes as string[];
      }

      if (prescriptionId && prescriberId) {
        filters.prescriptionParams = {
          prescriptionId,
          doctorId: prescriberId,
        };
      }

      const bannerData = await fetchActiveBanners(filters);
      setBanners(bannerData);
    } catch (err) {
      logger.error('Erro ao buscar banners:', err);
      setError('Erro ao carregar banners');
      setBanners([{ mobile: '', desktop: '', alt: 'Banner padrão', idBanner: 0 }]);
    } finally {
      setLoading(false);
    }
  }, [partnerId, patientAge, doctorSpecialities, prescriberId, prescriptionId]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const currentBanner = banners[currentBannerIndex];
  const allBannerIds = banners.map((banner) => banner.idBanner).filter((id) => id !== undefined);

  const trackBannerIsActive = useCallback(async () => {
    if (prescriptionId && !hasTrackedBanner.current) {
      hasTrackedBanner.current = true;
      await TrackerEventService.track(prescriptionId, 'backoffice_campaign_ads_exibiu', {
        idPrescricao: prescriptionId,
        idMedico: prescriberId,
        codigoValidacao: validationCode,
        idParceiro: partnerId,
        idBanner: allBannerIds,
        isMobile,
        timeStamp: DateTime.now(),
      });
    }
  }, [prescriptionId, prescriberId, validationCode, partnerId, allBannerIds, isMobile]);

  const handleBannerClick = useCallback(async () => {
    if (currentBanner?.link) {
      window.open(currentBanner.link, '_blank', 'noopener,noreferrer');

      if (prescriptionId) {
        amplitudeModule.bannerClicked({
          screen_name: 'prescrição',
          partner_id: partnerId,
          partner_name: partnerName ?? '',
          establishment_name: establishmentName ?? '',
          banner_type: 'patrocinado',
          banner_id: currentBanner?.idBanner ?? 0,
        });
        
        await TrackerEventService.track(prescriptionId, 'backoffice_campaign_ads_exibiu_clicou', {
          idPrescricao: prescriptionId,
          idMedico: prescriberId,
          codigoValidacao: validationCode,
          idParceiro: partnerId,
          idBanner: currentBanner?.idBanner,
          isMobile,
          timeStamp: DateTime.now(),
        });
      }
    }
  }, [currentBanner, prescriptionId, prescriberId, validationCode, partnerId, partnerName, establishmentName, isMobile]);

  useEffect(() => {
    if (banners.length > 0) {
      if (hasTrackedBanner.current) return;
      trackBannerIsActive();
    }
  }, [banners, allBannerIds, trackBannerIsActive, hasTrackedBanner]);

  useEffect(() => {
    const timeoutInterval = parseInt(process.env.REACT_APP_BANNER_CHANGE_TIMEOUT || '15000');

    if (banners.length > 1) {
      const timer = setInterval(() => {
        setCurrentBannerIndex((prevIndex) => {
          return (prevIndex + 1) % banners.length;
        });
      }, timeoutInterval);

      return () => {
        clearInterval(timer);
      };
    }
  }, [banners.length]);

  const handleCloseBanner = useCallback(async () => {
    if (prescriptionId) {
      await TrackerEventService.track(prescriptionId, 'backoffice_campaign_ads_exibiu_fechou', {
        idPrescricao: prescriptionId,
        idMedico: prescriberId,
        codigoValidacao: validationCode,
        idParceiro: partnerId,
        idBanner: currentBanner?.idBanner,
        isMobile,
        timeStamp: DateTime.now(),
      });
    }
    closeBanner();
  }, [prescriptionId, prescriberId, validationCode, partnerId, currentBanner?.idBanner, isMobile, closeBanner]);

  const CloseButton = (
    <Box
      position="absolute"
      top="-3"
      right="-3"
      zIndex="1"
      onClick={(e) => {
        e.stopPropagation();
        handleCloseBanner();
      }}
      cursor="pointer"
      p="16px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        bg="rgba(255, 255, 255, 0.8)"
        borderRadius="full"
        minW="16px"
        h="16px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        _hover={{ bg: 'rgba(255, 255, 255, 1)' }}
      >
        <Box boxSize="12px">
          <CloseIcon />
        </Box>
      </Box>
    </Box>
  );

  if (loading || error || !currentBanner || banners.length === 0) {
    return null;
  }

  const bannerUrl = isMobile
    ? currentBanner.mobile || currentBanner.desktop
    : currentBanner.desktop || currentBanner.mobile;

  return isMobile ? (
    <Box margin={4} placeItems={'center'}>
      <Flex placeContent={'flex-end'} position="relative">
        <Link onClick={handleBannerClick} cursor={currentBanner.link ? 'pointer' : 'default'}>
          <img src={bannerUrl} alt={currentBanner.alt} />
        </Link>
        {CloseButton}
      </Flex>
    </Box>
  ) : (
    <Box maxW={'480px'} h={'60px'} overflow={'hidden'}>
      <Flex placeContent={'flex-end'} position="relative" h={'100%'}>
        <Link onClick={handleBannerClick} cursor={currentBanner.link ? 'pointer' : 'default'} h={'100%'}>
          <img src={bannerUrl} alt={currentBanner.alt} style={{ height: '100%', width: 'auto', display: 'block' }} />
        </Link>
        {CloseButton}
      </Flex>
    </Box>
  );
};

export default BannerComponent;
